import { useTheme } from "@mui/material";

export const useModalStyles = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  /* ── Color tokens ── */
  const tokens = {
    bg: isDark ? "#141720" : "#ffffff",
    menuBg: isDark ? "#212534" : "#ffffff",
    headerFooterBg: isDark ? "transparent" : "#fafbfc",
    border: isDark ? "rgba(255,255,255,0.07)" : "#e5e9ed",
    textPrimary: isDark ? "#e2e8f0" : "#1e293b",
    textSecondary: isDark ? "#64748b" : "#64748b",
    inputBg: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9",
    inputBorder: isDark ? "rgba(255,255,255,0.10)" : "#cbd5e1",
    cardBg: isDark ? "#1c2030" : "#f1f5f9",
    accent: "#14b8a6",
    accentHover: "#0d9488",
    error: "#ef4444",
  };

  /* ── Dialog Paper sx ── */
  const paperSx = (fullScreen = false) => ({
    backgroundColor: tokens.bg,
    backgroundImage: "none",
    borderRadius: fullScreen ? 0 : "16px",
    border: `1px solid ${tokens.border}`,
    boxShadow: isDark
      ? "0 32px 80px rgba(0,0,0,0.5)"
      : "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    maxHeight: "92vh",
  });

  /* ── Header sx ── */
  const headerSx = {
    px: 2.8,
    pt: 2.5,
    pb: 2,
    display: "flex",
    alignItems: "flex-start",
    gap: 1.6,
    borderBottom: `1px solid ${tokens.border}`,
    backgroundColor: tokens.headerFooterBg,
    flexShrink: 0,
    position: "relative" as const,
  };

  /* ── Header icon box — pass hex color e.g. "#14b8a6" ── */
  const iconBoxSx = (hexColor: string) => ({
    width: 46,
    height: 46,
    borderRadius: "12px",
    backgroundColor: `${hexColor}1a`,
    border: `1px solid ${hexColor}33`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  });

  /* ── Scrollable form body sx ── */
  const bodySx = {
    px: 2.8,
    py: 2.4,
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
    flex: 1,
    overflowY: "auto" as const,
  };

  /* ── Themed scroll region for modal lists (dark + light) ── */
  const scrollAreaSx = (options?: { maxHeight?: string | number; gap?: number }) => {
    const trackBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
    const thumbBg = isDark ? "rgba(20,184,166,0.38)" : "rgba(20,184,166,0.48)";
    const thumbHoverBg = isDark ? "rgba(20,184,166,0.58)" : "rgba(20,184,166,0.68)";
    const thumbBorder = isDark ? tokens.bg : "#ffffff";

    return {
      display: "flex",
      flexDirection: "column" as const,
      gap: options?.gap ?? 1,
      maxHeight: options?.maxHeight ?? "320px",
      overflowY: "auto" as const,
      overflowX: "hidden" as const,
      pr: 1,
      pb: 1,
      mr: -0.25,
      scrollbarGutter: "stable" as const,
      scrollbarWidth: "thin" as const,
      scrollbarColor: `${thumbBg} ${trackBg}`,
      "&::-webkit-scrollbar": {
        width: 8,
      },
      "&::-webkit-scrollbar-track": {
        background: trackBg,
        borderRadius: "10px",
        margin: "6px 0",
      },
      "&::-webkit-scrollbar-thumb": {
        background: thumbBg,
        borderRadius: "10px",
        border: `2px solid ${thumbBorder}`,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: thumbHoverBg,
      },
    };
  };

  /* ── Footer sx ── */
  const footerSx = {
    px: 2.8,
    pb: 2.4,
    pt: 2,
    flexShrink: 0,
    backgroundColor: tokens.headerFooterBg,
    borderTop: `1px solid ${tokens.border}`,
  };

  /* ── Close icon button sx ── */
  const closeIconSx = {
    position: "absolute" as const,
    top: 10,
    right: 10,
    color: tokens.textSecondary,
    "&:hover": { color: tokens.textPrimary },
  };

  /* Opaque fill for webkit-autofill (rgba inputBg does not work with inset box-shadow) */
  const autofillInputBg = isDark ? "#1c2030" : "#f8fafc";

  /* Wrappers below hide the MUI label, so the notched outline must not reserve a
     gap for it — otherwise the field draws a break in its top border with nothing in it. */
  const collapsedNotchSx = {
    "& .MuiOutlinedInput-notchedOutline legend": {
      maxWidth: "0.01px !important",
    },
  };

  const autofillSx = {
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: `${autofillInputBg} !important`,
      "& fieldset": {
        borderColor: `${tokens.inputBorder} !important`,
      },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
      WebkitTextFillColor: `${tokens.textPrimary} !important`,
      caretColor: tokens.textPrimary,
      WebkitBoxShadow: `0 0 0 100px ${autofillInputBg} inset !important`,
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
  };

  /* ── Dropdown (CustomDropdownField) wrapper sx ── */
  const dropdownWrapSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${tokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: tokens.textPrimary,
      minHeight: "unset !important",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: `${hasError ? tokens.error : tokens.inputBorder} !important`,
      transition: "border-color 0.18s",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputLabel-root, & label": { display: "none" },
    "& .MuiSvgIcon-root": { color: tokens.textSecondary },
    "& .MuiInputBase-input": { color: tokens.textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(13,148,136,0.12)",
      color: `${tokens.accent} !important`,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
      fontWeight: 600,
    },
    "& .MuiChip-deleteIcon": {
      color: isDark ? "rgba(20,184,166,0.7)" : "rgba(13,148,136,0.7)",
    },
    ...collapsedNotchSx,
    ...autofillSx,
  });

  /* ── DatePicker (CustomDatePicker) wrapper sx ── */
  const datepickerWrapSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: `${tokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: tokens.textPrimary,
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      "& fieldset": {
        borderColor: `${hasError ? tokens.error : tokens.inputBorder} !important`,
        transition: "border-color 0.18s",
      },
      "&:hover fieldset": {
        borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
      },
      "&.Mui-focused fieldset": {
        borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
        borderWidth: "1px !important",
      },
      "&.Mui-focused": {
        boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
      },
    },
    "& .MuiInputLabel-root": { display: "none" },
    "& .MuiInputBase-input": { color: tokens.textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    "& .MuiSvgIcon-root": { color: tokens.textSecondary },
    "& .MuiInputBase-root": { paddingTop: "0 !important" },
    ...collapsedNotchSx,
    ...autofillSx,
  });

  /* ── DatePicker wrapper: same as datepickerWrapSx but keeps MUI labels & helper text visible ── */
  const datepickerWrapWithLabelSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: `${tokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: tokens.textPrimary,
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      "& fieldset": {
        borderColor: `${hasError ? tokens.error : tokens.inputBorder} !important`,
        transition: "border-color 0.18s",
      },
      "&:hover fieldset": {
        borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
      },
      "&.Mui-focused fieldset": {
        borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
        borderWidth: "1px !important",
      },
      "&.Mui-focused": {
        boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
      },
    },
    "& .MuiInputLabel-root, & .MuiFormLabel-root": {
      color: tokens.textSecondary,
      "&.Mui-focused": { color: tokens.accent },
    },
    "& .MuiFormHelperText-root": {
      color: tokens.textSecondary,
    },
    "& .MuiInputBase-input": { color: tokens.textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    "& .MuiSvgIcon-root": { color: tokens.textSecondary },
    ...autofillSx,
  });

  /* ── Text input (CustomInputField / CustomNumberField / CustomTextField) sx ── */
  const inputFieldSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${tokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: tokens.textPrimary,
      minHeight: "unset !important",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: `${hasError ? tokens.error : tokens.inputBorder} !important`,
      transition: "border-color 0.18s",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiOutlinedInput-root.Mui-disabled fieldset, & .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: `${tokens.inputBorder} !important`,
    },
    "& .MuiInputLabel-root, & label": { display: "none" },
    "& .MuiInputBase-input": { color: tokens.textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    ...collapsedNotchSx,
    ...autofillSx,
  });

  /* ── Dropdown wrapper: same as dropdownWrapSx but keeps MUI labels visible ── */
  const dropdownWrapWithLabelSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${tokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: tokens.textPrimary,
      minHeight: "unset !important",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: `${hasError ? tokens.error : tokens.inputBorder} !important`,
      transition: "border-color 0.18s",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputLabel-root": {
      color: tokens.textSecondary,
      "&.Mui-focused": { color: tokens.accent },
    },
    "& .MuiSvgIcon-root": { color: tokens.textSecondary },
    "& .MuiInputBase-input": { color: tokens.textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(13,148,136,0.12)",
      color: `${tokens.accent} !important`,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
      fontWeight: 600,
    },
    "& .MuiChip-deleteIcon": {
      color: isDark ? "rgba(20,184,166,0.7)" : "rgba(13,148,136,0.7)",
    },
    ...autofillSx,
  });

  /* ── Text input: same as inputFieldSx but keeps MUI labels & helper text visible ── */
  const inputFieldWithLabelSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${tokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: tokens.textPrimary,
      minHeight: "unset !important",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: `${hasError ? tokens.error : tokens.inputBorder} !important`,
      transition: "border-color 0.18s",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${hasError ? tokens.error : tokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiOutlinedInput-root.Mui-disabled fieldset, & .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: `${tokens.inputBorder} !important`,
    },
    "& .MuiInputLabel-root, & .MuiFormLabel-root": {
      color: tokens.textSecondary,
      "&.Mui-focused": { color: tokens.accent },
    },
    "& .MuiFormHelperText-root": {
      color: tokens.textSecondary,
    },
    "& .MuiInputBase-input": { color: tokens.textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    ...autofillSx,
  });

  /* ── Modern field wrappers ──
     Pair these with an external label above the control (the MUI label stays hidden).
     They pin every control to a single 42px height so labels, inputs and inline
     buttons line up across a form row. Not for multiline inputs. */
  const controlHeightSx = {
    minHeight: "42px !important",
    height: "42px !important",
    maxHeight: "42px !important",
    boxSizing: "border-box !important",
    alignItems: "center",
  };

  const fieldWrapSx = (hasError = false) => {
    const base = inputFieldSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root": {
        width: "100%",
        maxWidth: "100%",
      },
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
        ...(base["& .MuiOutlinedInput-root, & .MuiInputBase-root"] as object),
        ...controlHeightSx,
      },
      "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
        padding: "0 38px 0 12px !important",
        flexWrap: "nowrap !important",
        overflow: "hidden !important",
        ...controlHeightSx,
      },
      "& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
        paddingTop: "8px !important",
        paddingBottom: "8px !important",
        height: "24px !important",
        lineHeight: "24px !important",
        boxSizing: "border-box",
      },
      "& .MuiChip-root": { height: "24px !important", maxHeight: "24px !important" },
      "& .MuiAutocomplete-endAdornment": {
        top: "50% !important",
        transform: "translateY(-50%)",
        right: "8px !important",
      },
      "& .MuiFormHelperText-root": { marginLeft: 0, marginRight: 0, marginTop: "4px" },
    };
  };

  const dateFieldWrapSx = (hasError = false) => {
    const base = datepickerWrapSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiFormControl-root, & .MuiTextField-root": { width: "100%" },
      "& .MuiOutlinedInput-root": {
        ...(base["& .MuiOutlinedInput-root"] as object),
        ...controlHeightSx,
      },
      "& .MuiFormHelperText-root": { marginLeft: 0, marginRight: 0, marginTop: "4px" },
    };
  };

  /* ── Primary (teal) action button sx ── */
  const primaryBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: "10px",
    py: 1.2,
    color: "#ffffff",
    background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
    boxShadow: "0 4px 18px rgba(20,184,166,0.35)",
    "&:hover": {
      color: "#ffffff",
      background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
      boxShadow: "0 6px 22px rgba(20,184,166,0.45)",
    },
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "#ffffff",
    },
  };

  /* ── Destructive (red) action button sx ── */
  const destructiveBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: "10px",
    py: 1.2,
    backgroundColor: tokens.error,
    boxShadow: "0 4px 18px rgba(239,68,68,0.3)",
    "&:hover": {
      backgroundColor: "#dc2626",
      boxShadow: "0 6px 22px rgba(239,68,68,0.4)",
    },
  };

  /* ── Cancel (red outlined) button sx ── */
  const cancelBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: "10px",
    py: 1.2,
    color: tokens.error,
    borderColor: "rgba(239,68,68,0.4)",
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: tokens.error,
      backgroundColor: "rgba(239,68,68,0.06)",
      boxShadow: "none",
    },
  };

  /* ── Back (grey outlined) button sx ── */
  const backBtnSx = {
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "0.9rem",
    borderRadius: "10px",
    py: 1.2,
    color: tokens.textSecondary,
    borderColor: tokens.border,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: tokens.accent,
      color: tokens.accent,
      backgroundColor: "rgba(20,184,166,0.05)",
      boxShadow: "none",
    },
  };

  /* ── Inline ← Back text link sx ── */
  const backLinkSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.6,
    cursor: "pointer",
    userSelect: "none" as const,
    color: tokens.textSecondary,
    transition: "color 0.18s",
    "&:hover": { color: tokens.accent },
  };

  const filterToggleBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap" as const,
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  return {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    dropdownWrapWithLabelSx,
    datepickerWrapSx,
    datepickerWrapWithLabelSx,
    inputFieldSx,
    inputFieldWithLabelSx,
    fieldWrapSx,
    dateFieldWrapSx,
    primaryBtnSx,
    destructiveBtnSx,
    cancelBtnSx,
    backBtnSx,
    backLinkSx,
    filterToggleBtnSx,
    scrollAreaSx,
  };
};
